/**
 * RequireAuth -- Auth guard for browser routes.
 *
 * Supports two usages:
 *
 * 1. Single-route wrapper (children-based):
 *      <Route element={<RequireAuth><X /></RequireAuth>} />
 *
 * 2. Layout-route element (group-based, falls back to <Outlet/>):
 *      <Route element={<RequireAuth />}>
 *        <Route path="/account" ... />
 *        <Route path="/settings" ... />
 *      </Route>
 *
 * Loading state shows a spinner. Logged-out → Navigate to /login (replace,
 * so the back button doesn't loop). Logged-in renders children when
 * provided, otherwise <Outlet/> for the layout-route case.
 *
 * The /login redirect target is what AuthGate's old "/" target was replaced
 * with — login is now its own dedicated page in browser context.
 */

import { type ReactNode } from 'react'
import { Navigate, Outlet } from 'react-router-dom'
import { useAuth } from '@/lib/auth'
import { LoadingSpinner } from '@/shared/components/feedback/LoadingSpinner'

interface RequireAuthProps {
  children?: ReactNode
}

export function RequireAuth({ children }: RequireAuthProps) {
  const { isLoading, isLoggedIn } = useAuth()

  if (isLoading) {
    return (
      <div className="flex min-h-[calc(100vh-10rem)] items-center justify-center bg-ue-bg-darkest">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!isLoggedIn) {
    return <Navigate to="/login" replace />
  }

  return <>{children ?? <Outlet />}</>
}
